import { useState, useRef, useEffect, useCallback } from 'react'
import { clsx } from 'clsx'

export interface TagItem {
  id: string | number
  name: string
}

export interface CreatableTagInputProps {
  value: TagItem[]
  onSearch: (query: string) => Promise<TagItem[]>
  onCreate: (name: string) => Promise<TagItem>
  onAdd: (item: TagItem) => void
  onRemove: (item: TagItem) => void
  placeholder?: string
  createLabel?: string
  disabled?: boolean
  className?: string
}

export function CreatableTagInput({
  value,
  onSearch,
  onCreate,
  onAdd,
  onRemove,
  placeholder = 'Search or type to create...',
  createLabel = 'Create',
  disabled = false,
  className,
}: CreatableTagInputProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<TagItem[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearch('')
        setResults([])
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const doSearch = useCallback(
    async (query: string) => {
      if (!query.trim()) {
        setResults([])
        return
      }
      setIsSearching(true)
      try {
        const items = await onSearch(query)
        // Filter out already-selected items
        const selectedIds = new Set(value.map((v) => String(v.id)))
        setResults(items.filter((item) => !selectedIds.has(String(item.id))))
      } catch {
        setResults([])
      } finally {
        setIsSearching(false)
      }
    },
    [onSearch, value],
  )

  const handleSearchChange = (query: string) => {
    setSearch(query)
    setIsOpen(true)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => doSearch(query), 300)
  }

  const handleSelect = (item: TagItem) => {
    onAdd(item)
    setSearch('')
    setResults([])
    inputRef.current?.focus()
  }

  const handleCreate = async () => {
    if (!search.trim() || isCreating) return
    setIsCreating(true)
    try {
      const newItem = await onCreate(search.trim())
      onAdd(newItem)
      setSearch('')
      setResults([])
      inputRef.current?.focus()
    } catch {
      // silently fail
    } finally {
      setIsCreating(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false)
      setSearch('')
      setResults([])
    }
    if (e.key === 'Enter' && search.trim()) {
      e.preventDefault()
      // If there's an exact match in results, select it; otherwise create
      const exactMatch = results.find(
        (r) => r.name.toLowerCase() === search.trim().toLowerCase(),
      )
      if (exactMatch) {
        handleSelect(exactMatch)
      } else {
        handleCreate()
      }
    }
  }

  const hasExactMatch = results.some(
    (r) => r.name.toLowerCase() === search.trim().toLowerCase(),
  )
  const showCreateOption = search.trim() && !hasExactMatch && !isSearching

  return (
    <div className={clsx('relative w-full', className)} ref={containerRef}>
      <div
        className={clsx(
          'input min-h-[42px] flex flex-wrap gap-1.5 items-center cursor-text p-2',
          disabled && 'opacity-50 cursor-not-allowed',
          isOpen && 'ring-2 ring-primary-500/20 border-primary-500',
        )}
        onClick={() => {
          if (!disabled) {
            setIsOpen(true)
            inputRef.current?.focus()
          }
        }}
      >
        {value.map((item) => (
          <span
            key={item.id}
            className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full text-sm"
          >
            {item.name}
            {!disabled && (
              <button
                type="button"
                className="hover:text-primary-900 focus:outline-none"
                onClick={(e) => {
                  e.stopPropagation()
                  onRemove(item)
                }}
              >
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-sm p-0 focus:ring-0"
          placeholder={value.length === 0 ? placeholder : ''}
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />
      </div>

      {isOpen && !disabled && (results.length > 0 || showCreateOption) && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-secondary-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {results.map((item) => (
            <div
              key={item.id}
              className="px-3 py-2 cursor-pointer hover:bg-secondary-50 text-sm"
              onClick={() => handleSelect(item)}
            >
              {item.name}
            </div>
          ))}
          {showCreateOption && (
            <div
              className="px-3 py-2 cursor-pointer hover:bg-primary-50 text-sm text-primary-600 font-medium border-t border-secondary-100"
              onClick={handleCreate}
            >
              {isCreating ? 'Creating...' : `${createLabel}: "${search.trim()}"`}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
